import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { BaseDataService } from '../base-data/base-data.service';
import { ErrorDialogService } from '../../dialogs/error/error-dialog.service';
import { Options } from '../base-data/_models/base-data-options.model';
import { Secure } from '../../enums/secure.enum';
import { UrlUtil } from '../../util/url/url.util';
import { BasePasswordResetRequest } from '../../models/base-user/base-password-reset-request.model';
import { BaseUser } from '../../models/base-user/base-user.model';
import { LeathermanAppConfigInjectionToken } from '../../config/injection-tokens/leatherman-app-config.injection-token';
import { ILeathermanAppConfig } from '../../config/app.config';
import { ErrorDialogServiceInjectionToken } from '../../config/injection-tokens/error-dialog-service.injection-token';
import { ValidatePasswordResetTokenResponse } from '../../models/base-user/validate-password-reset-token-response.model';
import { PasswordReset } from '../../models/base-user/password-reset.model';
import { GetOptions } from '../../models/args/get-options.model';
import { ValidateVerifyEmailTokenResponse } from '../../models/base-user/validate-verify-email-token-response.model';

/**
 * The base user data service
 */
@Injectable({
    providedIn: 'root'
})
export class BaseUserDataService extends BaseDataService {
    /**
     * Constructor
     * @param config - The app config object
     * @param errorDialogService - A reference to the error dialog service
     * @param router - A reference to the router
     * @param httpClient - A reference to the HTTP client service
     */
    constructor(
        @Inject(LeathermanAppConfigInjectionToken)
        protected config: ILeathermanAppConfig,
        @Inject(ErrorDialogServiceInjectionToken)
        errorDialogService: ErrorDialogService,
        router: Router,
        httpClient: HttpClient
    ) {
        super(config, errorDialogService, router, httpClient);
        this.baseEndpoint = config.userBaseEndpoint;
        this.type = BaseUser;
    }

    /**
     * Add a role to a user
     * @param userId - The user's ID
     * @param role - The role to add to the user
     * @returns The updated user
     * @async
     */
    public async _addUserRole<T extends BaseUser>(
        userId: string,
        role: string
    ): Promise<T> {
        const options = new Options();
        options.secure = Secure.true;
        options.url = UrlUtil.join(
            this.apiRoot,
            this.baseEndpoint,
            userId,
            'addrole'
        );
        options.parameters.add('role', role);
        return this._putWithOptions<T>(options).catch(async err => {
            return await this.handlePromiseError(
                'UserDataService',
                'addUserRole',
                err
            );
        });
    }

    /**
     * Delete a user
     * @param userId - The ID of the user to delete
     * @returns True if the delete succeeded
     * @async
     */
    public async _deleteUser(userId: string): Promise<boolean> {
        return this._delete(userId, Secure.true).catch(async err => {
            return await this.handlePromiseError(
                'UserDataService',
                'deleteUser',
                err
            );
        });
    }

    /**
     * Get all admin users
     * @returns An array of admin users
     * @asyncs
     */
    public async _getAdminUsers<T extends BaseUser>(
        options?: GetOptions
    ): Promise<T[]> {
        const getManyOptions = new Options();
        getManyOptions.secure = Secure.true;
        getManyOptions.url = UrlUtil.join(this.apiRoot, this.baseEndpoint);
        getManyOptions.parameters.add('role', 'admin');
        if (options) {
            getManyOptions.parameters.add('skip', options.skip.toString());
            getManyOptions.parameters.add('trace', options.take.toString());
            if (options.sort.length > 0) {
                getManyOptions.parameters.add('sort', options.sort);
            }
        }
        const adminUsers = await this._getManyWithOptions<T>(getManyOptions).catch(
            async err => {
                return await this.handlePromiseError(
                    'UserDataService',
                    'getAdminUsers',
                    err
                );
            }
        );
        return adminUsers;
    }

    /**
     * Get a user
     * @param id - The ID of the user to get
     * @returns A user object
     * @async
     */
    public async _getUser<T extends BaseUser>(id: string): Promise<T> {
        const options = new Options();
        options.secure = Secure.true;
        options.url = UrlUtil.join(this.apiRoot, this.baseEndpoint, id);
        const user = await this._getWithOptions<T>(options).catch(async err => {
            return await this.handlePromiseError('UserDataService', 'getUser', err);
        });
        return user;
    }

    // getBasicUsers
    public async _getBasicUsers<T extends BaseUser>(
        options?: GetOptions
    ): Promise<T[]> {
        const getManyOptions = new Options();
        getManyOptions.secure = Secure.true;
        getManyOptions.url = UrlUtil.join(this.apiRoot, this.baseEndpoint);
        getManyOptions.parameters.add('onlyrole', 'user');
        if (options) {
            getManyOptions.parameters.add('skip', options.skip.toString());
            getManyOptions.parameters.add('trace', options.take.toString());
            if (options.sort.length > 0) {
                getManyOptions.parameters.add('sort', options.sort);
            }
        }
        const assessmentUsers = await this._getManyWithOptions<T>(
            getManyOptions
        ).catch(async err => {
            return await this.handlePromiseError(
                'UserDataService',
                'getAssessmentUsers',
                err
            );
        });
        return assessmentUsers;
    }

    /**
     * Get all users
     * @returns An array of users
     * @async
     */
    public async _getUsers<T extends BaseUser>(
        options?: GetOptions
    ): Promise<T[]> {
        const getManyOptions = new Options();
        getManyOptions.secure = Secure.true;
        getManyOptions.url = UrlUtil.join(this.apiRoot, this.baseEndpoint);
        if (options) {
            getManyOptions.parameters.add('skip', options.skip.toString());
            getManyOptions.parameters.add('trace', options.take.toString());
            if (options.sort.length > 0) {
                getManyOptions.parameters.add('sort', options.sort);
            }
        }
        const assessmentUsers = await this._getManyWithOptions<T>(
            getManyOptions
        ).catch(async err => {
            return await this.handlePromiseError('UserDataService', 'getUsers', err);
        });
        return assessmentUsers;
    }

    /**
     * Remove a role from a user
     * @param userId - The ID of the user
     * @param role - The role to remove from the user
     */
    public async _removeUserRole<T extends BaseUser>(
        userId: string,
        role: string
    ): Promise<T> {
        const options = new Options();
        options.secure = Secure.true;
        options.url = UrlUtil.join(
            this.apiRoot,
            this.baseEndpoint,
            userId,
            'remove-role'
        );
        options.parameters.add('role', role);
        return this._putWithOptions<T>(options).catch(async err => {
            return await this.handlePromiseError(
                'UserDataService',
                'removeUserRole',
                err
            );
        });
    }

    /**
     * Request a password reset
     * @param passwordResetRequest - A password reset request object
     * @returns True if the request succeeded
     * @async
     */
    public async _requestPasswordReset(
        passwordResetRequest: BasePasswordResetRequest
    ): Promise<boolean> {
        const options = new Options();
        options.secure = Secure.false;
        options.url = UrlUtil.join(
            this.apiRoot,
            this.baseEndpoint,
            'request-password-reset'
        );
        options.objectType = BasePasswordResetRequest;
        await this._postWithOptions<boolean>(passwordResetRequest, options).catch(
            async err => {
                return await this.handlePromiseError(
                    'UserDataService',
                    'requestPasswordReset',
                    err
                );
            }
        );
        return true;
    }

    /**
     * Request a password reset token. This method is only used for testing.
     * @param passwordResetRequest - A password reset request object
     * @returns A password reset token
     * @async
     */
    public async _requestPasswordResetToken(
        passwordResetRequest: BasePasswordResetRequest
    ): Promise<string> {
        const options = new Options();
        options.secure = Secure.false;
        options.url = UrlUtil.join(
            this.apiRoot,
            this.baseEndpoint,
            'request-password-reset-token'
        );
        options.objectType = BasePasswordResetRequest;
        const passwordResetToken = await this._postWithOptions<string>(
            passwordResetRequest,
            options
        ).catch(async err => {
            return await this.handlePromiseError(
                'UserDataService',
                'requestPasswordResetToken',
                err
            );
        });
        return passwordResetToken;
    }

    /**
     * Resend Verification email
     * @param verifyEmail - A verification email
     * @returns True if the verification email is sent
     */
    public async _resendVerificationEmail(
        verificationEmail: string
    ): Promise<boolean> {
        const options = new Options();
        options.secure = Secure.false;
        options.url = UrlUtil.join(
            this.apiRoot,
            this.baseEndpoint,
            'resend-verification-email'
        );
        options.parameters.add('email', verificationEmail);
        return this._getWithOptions<boolean>(
            options
        ).catch(async err => {
            return await this.handlePromiseError(
                'UserDataService',
                'resendVerificationEmail',
                err
            );
        });
    }

    /**
     * Reset a password
     * @param passwordReset - A password reset object
     * @returns True if the password was successfully reset
     * @async
     */
    public async _resetPassword(passwordReset: PasswordReset): Promise<boolean> {
        const options = new Options();
        options.secure = Secure.false;
        options.url = UrlUtil.join(
            this.apiRoot,
            this.baseEndpoint,
            'reset-password'
        );
        const result = await this._postWithOptions<boolean>(passwordReset, options);
        return result;
    }

    /**
     * Update a user
     * @param user - The user to update
     * @returns The updated user
     * @async
     */
    public async _updateUser<T extends BaseUser>(user: T): Promise<T> {
        return this._put<T>(user, Secure.true).catch(async err => {
            return await this.handlePromiseError(
                'UserDataService',
                'updateUser',
                err
            );
        });
    }

    /**
     * Validate a password reset token
     * @param passwordResetToken - The password reset token to validate
     * @returns A validate password reset token response
     * @async
     */
    public async _validatePasswordResetToken(
        passwordResetToken: string
    ): Promise<ValidatePasswordResetTokenResponse> {
        const options = new Options();
        options.secure = Secure.false;
        options.url = UrlUtil.join(
            this.apiRoot,
            this.baseEndpoint,
            'validate-password-reset-token'
        );
        options.parameters.add('token', passwordResetToken);
        return this._getWithOptions<ValidatePasswordResetTokenResponse>(
            options
        ).catch(async err => {
            return await this.handlePromiseError(
                'UserDataService',
                'validatePasswordResetToken',
                err
            );
        });
    }

    /**
     * Validate an email address
     * @param email - The email address to validate
     * @returns True if the email address is not currently in the user table
     * @async
     */
    public _validateEmail(email: string): Observable<boolean> {
        return this.httpClient.get<boolean>(
            UrlUtil.join(
                this.apiRoot,
                this.baseEndpoint,
                'validate-email' + '?email=' + email
            ),
            {
                headers: this.noTokenHeader
            }
        );
    }

    public async _validateVerifyEmailToken(
        verifyEmailToken: string
    ): Promise<ValidateVerifyEmailTokenResponse> {
        const options = new Options();
        options.secure = Secure.false;
        options.url = UrlUtil.join(
            this.apiRoot,
            this.baseEndpoint,
            'validate-verify-email-token'
        );
        options.parameters.add('token', verifyEmailToken);
        return this._getWithOptions<ValidateVerifyEmailTokenResponse>(
            options
        ).catch(async err => {
            return await this.handlePromiseError(
                'UserDataService',
                'validateVerifyEmailToken',
                err
            );
        });
    }
}
